import { useState, useCallback } from "react";
import { ResolvedWillOpenCard } from "../../helpers/willOpenCardHelper";
import {
  requestService,
  ConfirmSelectionRequestDto,
} from "src/api/request/requestService";
import { enqueueSnackbar } from "notistack";
import { useTranslate } from "src/locales";

interface UseRequestModalFlowProps {
  rowID: string;
  selectedOfficeId: number;
  setSelectedServices: React.Dispatch<React.SetStateAction<(number | string)[]>>;
  setSelectedHistoryServices?: React.Dispatch<React.SetStateAction<(number | string)[]>>;
  validationHook?: any; // To avoid circular dependency, strict typing might be hard initially
}

export const useRequestModalFlow = ({
  rowID,
  selectedOfficeId,
  setSelectedServices,
  setSelectedHistoryServices,
}: UseRequestModalFlowProps) => {
  const { t } = useTranslate();

  const [pendingWillOpenCards, setPendingWillOpenCards] = useState<
    ResolvedWillOpenCard[]
  >([]);
  const [activeWillOpenCardIndex, setActiveWillOpenCardIndex] = useState<number | null>(null);
  const [pendingConfirmSelection, setPendingConfirmSelection] = useState<{
    ptId: number;
    processId: number;
    isSelected: boolean;
    quantity: number;
    officeId?: number;
    serviceId: number | string;
    historyGridId?: number | string;
  } | null>(null);
  const [willOpenCardFlowValid, setWillOpenCardFlowValid] = useState(true);
  const [serviceAddInfo, setServiceAddInfo] = useState<Record<string, any>>({});
  const [activeWillOpenCardServiceName, setActiveWillOpenCardServiceName] =
    useState<string | undefined>(undefined);
  const [pendingAddToPatient, setPendingAddToPatient] = useState(false);

  // Modal States
  const [openHeightInfoModal, setOpenHeightInfoModal] = useState(false);
  const [openVkiModal, setOpenVkiModal] = useState(false); // Kept for types, though original uses VkiRequest/Add
  const [openVkiAddModal, setOpenVkiAddModal] = useState(false);
  const [openVkiRequestModal, setOpenVkiRequestModal] = useState(false);
  const [openEgfrModal, setOpenEgfrModal] = useState(false);
  const [openProcessDirectionModal, setOpenProcessDirectionModal] = useState(false);
  const [openAskDoctorModal, setOpenAskDoctorModal] = useState(false);
  const [openPregnantControlModal, setOpenPregnantControlModal] = useState(false);
  const [openHivModal, setOpenHivModal] = useState(false);
  const [openPacemakerModal, setOpenPacemakerModal] = useState(false);
  const [openConsultationModal, setOpenConsultationModal] = useState(false);
  const [openLabRequestModal, setOpenLabRequestModal] = useState(false);
  const [openPathologyModal, setOpenPathologyModal] = useState(false);
  const [openContrastModal, setOpenContrastModal] = useState(false);
  const [openContrastRequestModal, setOpenContrastRequestModal] = useState(false);
  const [openExaminationRequestModal, setOpenExaminationRequestModal] = useState(false);
  const [openPatientFileChangeModal, setOpenPatientFileChangeModal] = useState(false);

  // --- Helpers ---

  const upsertAddInfoForPendingService = useCallback(
    (key: string, data: any, explicitServiceId?: number | string) => {
      const serviceId = explicitServiceId ?? pendingConfirmSelection?.serviceId;
      if (serviceId === undefined || serviceId === null) return;
      setServiceAddInfo((prev) => {
        const current = prev[String(serviceId)] || {};
        return { ...prev, [String(serviceId)]: { ...current, [key]: data } };
      });
    },
    [pendingConfirmSelection]
  );

  const removeAddInfoForService = useCallback((serviceId: number | string) => {
    setServiceAddInfo((prev) => {
      const copy = { ...prev };
      delete copy[String(serviceId)];
      return copy;
    });
  }, []);

  const closeAllWillOpenCardModals = useCallback(() => {
    setOpenHeightInfoModal(false);
    setOpenVkiRequestModal(false);
    setOpenVkiAddModal(false);
    setOpenVkiModal(false);
    setOpenEgfrModal(false);
    setOpenProcessDirectionModal(false);
    setOpenAskDoctorModal(false);
    setOpenPregnantControlModal(false);
    setOpenHivModal(false);
    setOpenPacemakerModal(false);
    setOpenLabRequestModal(false);
    setOpenExaminationRequestModal(false);
    setOpenConsultationModal(false);
    setOpenPathologyModal(false);
    setOpenPatientFileChangeModal(false);
    setOpenContrastModal(false);
    setOpenContrastRequestModal(false);
  }, []);

  // --- Flow Logic ---

  // Defined here to be used recursively
  const handleWillOpenCardModalClosedState = async (explicitCurrentIndex?: number, externalValidationHook?: any, explicitPendingConfirm?: any) => {
      const currentIndex = explicitCurrentIndex ?? activeWillOpenCardIndex;
      const nextIndex = currentIndex !== null ? currentIndex + 1 : null;
      
      // Use explicit pending confirm if provided, otherwise state
      const currentPendingConfirm = explicitPendingConfirm ?? pendingConfirmSelection;

      if (
        nextIndex !== null &&
        pendingWillOpenCards.length &&
        nextIndex < pendingWillOpenCards.length
      ) {
        setActiveWillOpenCardIndex(nextIndex);
        openModalForCardState(pendingWillOpenCards[nextIndex], currentPendingConfirm, nextIndex);
      } else {
        // All cards closed
        setActiveWillOpenCardIndex(null);
        setPendingWillOpenCards([]);
        closeAllWillOpenCardModals();
        
        // Confirm Selection Flow
        if (currentPendingConfirm && willOpenCardFlowValid) {
             try {
              const confirmRequest: ConfirmSelectionRequestDto = {
                ptId: currentPendingConfirm.ptId,
                processId: currentPendingConfirm.processId,
                isSelected: currentPendingConfirm.isSelected,
                quantity: currentPendingConfirm.quantity,
                officeId: currentPendingConfirm.officeId,
              };
  
              const confirmResponse = await requestService.confirmSelection(confirmRequest);
              
              const isConfirmed = confirmResponse.isSuccess || (confirmResponse as any).issuccess || (confirmResponse as any).issuccessful;

              if (isConfirmed) {
                  enqueueSnackbar(t("patient-request-service.messages.service-selected"), { variant: "success" });
                  
                  const serviceIdToApply = currentPendingConfirm.serviceId;
                  const shouldSelect = currentPendingConfirm.isSelected;

                  setSelectedServices((prev) => {
                    if (shouldSelect) {
                         return prev.includes(serviceIdToApply) ? prev : [...prev, serviceIdToApply];
                    } else {
                         return prev.filter((id) => id !== serviceIdToApply);
                    }
                  });
              } else if (!currentPendingConfirm.isSelected) {
                   removeAddInfoForService(currentPendingConfirm.serviceId);
              }
             } catch (confirmError) {
                console.error("Confirm Selection Error:", confirmError);
                enqueueSnackbar("Hizmet seçimi onaylanırken hata oluştu", { variant: "error" });
             } finally {
                setPendingConfirmSelection(null);
             }
        } else if (pendingAddToPatient && willOpenCardFlowValid) {
            // Callback to Validation Hook if present
            if (externalValidationHook && externalValidationHook.handleAddToPatientClick) {
                externalValidationHook.handleAddToPatientClick();
            }
        } else {
            setPendingConfirmSelection(null);
            setPendingAddToPatient(false);
        }
      }
    };

    const openModalForCardState = (
        card?: any, 
        explicitPendingConfirm?: any, 
        explicitCurrentIndex?: number
    ) => {
        closeAllWillOpenCardModals();
        if (!card) {
          handleWillOpenCardModalClosedState(explicitCurrentIndex, undefined, explicitPendingConfirm);
          return;
        }

        // Logic check for existing data in serviceAddInfo
        let dataKeyToFind: string | null = null;
        switch (card.key) {
            case "HEIGHT_INFO": case "HEIGHT_INFO_REQUEST": dataKeyToFind = "HEIGHT_INFO"; break;
            case "EGFR": case "EGFR_REQUEST": dataKeyToFind = "EGFR"; break;
            case "PREGNANT_CONTROL": case "PREGNANT_CONTROL_REQUEST": dataKeyToFind = "PREGNANT_CONTROL"; break;
            case "PACEMAKER": case "PACEMAKER_CONTROL": case "PACEMAKER_CONTROL_REQUEST": dataKeyToFind = "PACEMAKER_CONTROL"; break;
        }

        if (dataKeyToFind) {
             const existingDataEntry = Object.values(serviceAddInfo).find(
                (info: any) => info && info[dataKeyToFind!]
              );
             if (existingDataEntry && (existingDataEntry as any)[dataKeyToFind!]) {
                  const targetServiceId = explicitPendingConfirm?.serviceId ?? pendingConfirmSelection?.serviceId;
                  if (targetServiceId) {
                      upsertAddInfoForPendingService(dataKeyToFind!, (existingDataEntry as any)[dataKeyToFind!], targetServiceId);
                  }
                  // Pass explicitPendingConfirm here too!
                  handleWillOpenCardModalClosedState(explicitCurrentIndex, undefined, explicitPendingConfirm);
                  return;
             }
        }
    
        // Open appropriate modal with timeout to ensure previous closes
        setTimeout(() => {
          switch (card.key) {
            case "HEIGHT_INFO": setOpenHeightInfoModal(true); break;
            case "HEIGHT_INFO_REQUEST": setOpenVkiRequestModal(true); break;
            case "VKI_REQUEST": setOpenVkiRequestModal(true); break;
            case "VKI_ADD": setOpenVkiAddModal(true); break;
            case "PROCESS_DIRECTION": case "PROCESS_DIRECTION_REQUEST": setOpenProcessDirectionModal(true); break;
            case "ASK_DOCTOR": case "ASK_DOCTOR_REQUEST": setOpenAskDoctorModal(true); break;
            case "PREGNANT_CONTROL": case "PREGNANT_CONTROL_REQUEST": setOpenPregnantControlModal(true); break;
            case "HIV": case "HIV_REQUEST": setOpenHivModal(true); break;
            case "PACEMAKER": case "PACEMAKER_CONTROL": case "PACEMAKER_CONTROL_REQUEST": setOpenPacemakerModal(true); break;
            case "CONSULTATION": case "CONSULTATION_REQUEST": setOpenConsultationModal(true); break;
            case "LAB_REQUEST": setOpenLabRequestModal(true); break;
            case "PATHOLOGY": case "PATHOLOGY_REQUEST": setOpenPathologyModal(true); break;
            case "PATIENT_FILE_CHANGE": setOpenPatientFileChangeModal(true); break;
            case "CONTRAST": setOpenContrastModal(true); break;
            case "CONTRAST_REQUEST": setOpenContrastRequestModal(true); break;
            case "EXAMINATION_REQUEST": setOpenExaminationRequestModal(true); break;
            case "EGFR": case "EGFR_REQUEST": setOpenEgfrModal(true); break;
            default: handleWillOpenCardModalClosedState();
          }
        }, 10);
      };

  const handleWillOpenCardModalCancelled = useCallback(() => {
    setWillOpenCardFlowValid(false);
    // Revert logic
    if (pendingConfirmSelection && pendingConfirmSelection.isSelected && pendingConfirmSelection.serviceId) {
      const serviceIdToRevert = pendingConfirmSelection.serviceId;
      const revertStr = String(serviceIdToRevert);
      setSelectedServices((prev) => prev.filter((id) => String(id) !== revertStr));
      
      if (pendingConfirmSelection.historyGridId) {
        const hGridId = String(pendingConfirmSelection.historyGridId);
        setSelectedHistoryServices?.((prev) => prev.filter((id) => String(id) !== hGridId));
      }

      removeAddInfoForService(serviceIdToRevert);
    }
    setActiveWillOpenCardIndex(null);
    setPendingWillOpenCards([]);
    setPendingConfirmSelection(null);
    setPendingAddToPatient(false);
    closeAllWillOpenCardModals();
  }, [pendingConfirmSelection, closeAllWillOpenCardModals, removeAddInfoForService, setSelectedServices, setSelectedHistoryServices]);

  return {
    pendingWillOpenCards, setPendingWillOpenCards,
    activeWillOpenCardIndex, setActiveWillOpenCardIndex,
    pendingConfirmSelection, setPendingConfirmSelection,
    willOpenCardFlowValid, setWillOpenCardFlowValid,
    serviceAddInfo, setServiceAddInfo,
    activeWillOpenCardServiceName, setActiveWillOpenCardServiceName,
    pendingAddToPatient, setPendingAddToPatient,
    
    // Modals
    openHeightInfoModal, setOpenHeightInfoModal,
    openVkiModal, setOpenVkiModal,
    openVkiAddModal, setOpenVkiAddModal,
    openVkiRequestModal, setOpenVkiRequestModal,
    openEgfrModal, setOpenEgfrModal,
    openProcessDirectionModal, setOpenProcessDirectionModal,
    openAskDoctorModal, setOpenAskDoctorModal,
    openPregnantControlModal, setOpenPregnantControlModal,
    openHivModal, setOpenHivModal,
    openPacemakerModal, setOpenPacemakerModal,
    openConsultationModal, setOpenConsultationModal,
    openLabRequestModal, setOpenLabRequestModal,
    openPathologyModal, setOpenPathologyModal,
    openContrastModal, setOpenContrastModal,
    openContrastRequestModal, setOpenContrastRequestModal,
    openExaminationRequestModal, setOpenExaminationRequestModal,
    openPatientFileChangeModal, setOpenPatientFileChangeModal,

    // Methods
    handleWillOpenCardModalClosed: handleWillOpenCardModalClosedState,
    openModalForCard: openModalForCardState,
    handleWillOpenCardModalCancelled,
    upsertAddInfoForPendingService,
    removeAddInfoForService,
    closeAllWillOpenCardModals
  };
};
